import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { supabase } from './supabaseClient';
import { diffRuns } from './utils/regressionHistory';
import { exportCSV } from './utils/format';

// 実行パラメータのラベル/バッジ表示ヘルパー（モデル以外の3軸）
const PARAM_BADGES = [
  { key: 'thinking_level', label: '思考', className: 'bg-purple-50 text-purple-700' },
  { key: 'temperature_mode', label: 'temp', className: 'bg-amber-50 text-amber-700' },
  { key: 'media_resolution', label: '解像度', className: 'bg-teal-50 text-teal-700' },
];

function paramSuffix(run) {
  return PARAM_BADGES
    .filter(({ key }) => run[key] && run[key] !== 'default')
    .map(({ key, label }) => `${label}:${run[key]}`)
    .join(' / ');
}

function paramGroupKey(run) {
  return [run.model, run.thinking_level || 'default', run.temperature_mode || 'default', run.media_resolution || 'default'].join('|');
}

export default function RegressionHistory({ companyId, refreshKey }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRun, setExpandedRun] = useState(null);
  const [runItems, setRunItems] = useState([]);
  const [diffResult, setDiffResult] = useState(null);
  const [diffRunIds, setDiffRunIds] = useState([null, null]);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('pm_regression_runs')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(50);
    setRuns(data || []);
    setLoading(false);
  }, [companyId]);

  const fmtRunLabel = (run) => {
    const suffix = paramSuffix(run);
    return `${fmtDate(run.created_at)} (${run.model}${suffix ? ` / ${suffix}` : ''})`;
  };

  useEffect(() => { fetchRuns(); }, [fetchRuns, refreshKey]);

  // モデル×思考レベル×temperature×画像解像度の組み合わせ別集計
  // 正答率として比較できるのは完了済みのリグレッション実行のみ（安定性実行は「一致」の意味が異なり、
  // 中止された実行は不完全なため、いずれも除外する）
  const paramGroups = useMemo(() => {
    const groups = new Map();
    for (const run of runs) {
      if (run.mode !== 'regression' || run.status !== 'completed') continue;
      const key = paramGroupKey(run);
      if (!groups.has(key)) {
        groups.set(key, {
          model: run.model,
          thinking_level: run.thinking_level || 'default',
          temperature_mode: run.temperature_mode || 'default',
          media_resolution: run.media_resolution || 'default',
          runCount: 0, labelCount: 0, matchCount: 0, errorCount: 0,
          medianSum: 0, medianN: 0,
        });
      }
      const g = groups.get(key);
      g.runCount += 1;
      g.labelCount += run.label_count || 0;
      g.matchCount += run.match_count || 0;
      g.errorCount += run.error_count || 0;
      if (run.latency_median_ms) { g.medianSum += run.latency_median_ms; g.medianN += 1; }
    }
    return [...groups.values()]
      .map(g => ({
        ...g,
        accuracy: g.labelCount > 0 ? (g.matchCount / g.labelCount) * 100 : null,
        avgMedianMs: g.medianN > 0 ? g.medianSum / g.medianN : null,
      }))
      .sort((a, b) => (b.accuracy ?? -1) - (a.accuracy ?? -1));
  }, [runs]);

  const loadItems = async (runId) => {
    if (expandedRun === runId) { setExpandedRun(null); return; }
    const { data } = await supabase
      .from('pm_regression_run_items')
      .select('*')
      .eq('run_id', runId)
      .order('created_at');
    setRunItems(data || []);
    setExpandedRun(runId);
  };

  const runDiff = async () => {
    if (!diffRunIds[0] || !diffRunIds[1]) { alert('比較する2つの実行を選択してください'); return; }
    const [{ data: a }, { data: b }] = await Promise.all([
      supabase.from('pm_regression_run_items').select('*').eq('run_id', diffRunIds[0]),
      supabase.from('pm_regression_run_items').select('*').eq('run_id', diffRunIds[1]),
    ]);
    setDiffResult(diffRuns(a || [], b || []));
  };

  const exportRunCSV = (items, run) => {
    const headers = ['ラベル名', '期待', '実際', '一致', '理由', 'ラベル整合', '個数照合', 'ラベル個数', '検出個数', '判定時間(秒)', 'エラー'];
    const rows = items.map(i => [
      i.label, i.expected_status || '', i.actual_status || '',
      i.match == null ? '' : i.match ? 'OK' : 'NG',
      i.reason || '', i.label_consistency || '', i.quantity_status || '',
      i.label_quantity_number ?? '', i.detected_quantity ?? '',
      i.analyze_ms ? (i.analyze_ms / 1000).toFixed(2) : '', i.error || '',
    ]);
    const date = new Date(run.created_at).toISOString().slice(0, 10);
    const tags = PARAM_BADGES.filter(({ key }) => run[key] && run[key] !== 'default').map(({ key }) => `_${run[key]}`).join('');
    exportCSV(headers, rows, `PM_履歴_${run.model || ''}${tags}_${date}.csv`);
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };

  if (loading) return <p className="text-xs text-slate-400 py-4 text-center">履歴読み込み中...</p>;
  if (runs.length === 0) return <p className="text-xs text-slate-400 py-4 text-center">実行履歴がありません</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs font-bold text-slate-500">実行履歴（最新50件）</p>

      {/* 差分比較 */}
      <div className="flex items-center gap-2 text-xs">
        <select value={diffRunIds[0] || ''} onChange={e => setDiffRunIds([e.target.value || null, diffRunIds[1]])} className="flex-1 border rounded px-1 py-0.5 text-xs">
          <option value="">比較元を選択</option>
          {runs.map(r => <option key={r.id} value={r.id}>{fmtRunLabel(r)}</option>)}
        </select>
        <span>→</span>
        <select value={diffRunIds[1] || ''} onChange={e => setDiffRunIds([diffRunIds[0], e.target.value || null])} className="flex-1 border rounded px-1 py-0.5 text-xs">
          <option value="">比較先を選択</option>
          {runs.map(r => <option key={r.id} value={r.id}>{fmtRunLabel(r)}</option>)}
        </select>
        <button onClick={runDiff} className="px-2 py-0.5 bg-indigo-600 text-white rounded text-xs font-bold">差分</button>
      </div>

      {diffResult && (
        <div className="bg-slate-50 border rounded p-2 space-y-1">
          <p className="text-xs font-bold text-slate-500">差分: {diffResult.length}件の変化</p>
          {diffResult.length === 0 && <p className="text-xs text-slate-400">変化なし</p>}
          {diffResult.map((d, i) => (
            <div key={i} className={`text-[10px] px-2 py-1 rounded ${d.type === 'changed' ? 'bg-amber-50' : d.type === 'added' ? 'bg-green-50' : 'bg-red-50'}`}>
              <span className="font-bold">{d.label}</span>
              {d.type === 'changed' && <span className="ml-2">{d.before?.actual_status}→{d.after?.actual_status} ({d.before?.match ? 'OK' : 'NG'}→{d.after?.match ? 'OK' : 'NG'})</span>}
              {d.type === 'added' && <span className="ml-2 text-green-700">追加</span>}
              {d.type === 'removed' && <span className="ml-2 text-red-700">削除</span>}
            </div>
          ))}
          <button onClick={() => setDiffResult(null)} className="text-[10px] text-slate-400 hover:text-slate-600">閉じる</button>
        </div>
      )}

      {/* パラメータ別集計（モデル×思考レベル×temperature×画像解像度） */}
      {paramGroups.length > 0 && (
        <div className="border rounded overflow-x-auto">
          <p className="text-xs font-bold text-slate-500 px-3 pt-2">パラメータ別集計（{paramGroups.length}パターン）</p>
          <table className="w-full text-[11px] mt-1">
            <thead>
              <tr className="text-slate-400 text-left border-b">
                <th className="px-3 py-1 font-normal">モデル</th>
                <th className="px-1 py-1 font-normal">思考</th>
                <th className="px-1 py-1 font-normal">temp</th>
                <th className="px-1 py-1 font-normal">解像度</th>
                <th className="px-2 py-1 font-normal text-right">実行</th>
                <th className="px-2 py-1 font-normal text-right">正答率</th>
                <th className="px-2 py-1 font-normal text-right">中央値検査時間</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {paramGroups.map((g, i) => (
                <tr key={i}>
                  <td className="px-3 py-1 font-mono text-slate-600">{g.model}</td>
                  <td className="px-1 py-1 text-slate-500">{g.thinking_level === 'default' ? '—' : g.thinking_level}</td>
                  <td className="px-1 py-1 text-slate-500">{g.temperature_mode === 'default' ? '—' : g.temperature_mode}</td>
                  <td className="px-1 py-1 text-slate-500">{g.media_resolution === 'default' ? '—' : g.media_resolution}</td>
                  <td className="px-2 py-1 text-right text-slate-500">{g.runCount}回 ({g.labelCount}件)</td>
                  <td className={`px-2 py-1 text-right font-bold ${g.accuracy != null && g.accuracy >= 95 ? 'text-green-600' : g.accuracy != null && g.accuracy < 90 ? 'text-red-600' : 'text-slate-700'}`}>
                    {g.accuracy != null ? `${g.accuracy.toFixed(1)}%` : '—'}
                  </td>
                  <td className="px-2 py-1 text-right text-slate-500">{g.avgMedianMs != null ? `${(g.avgMedianMs / 1000).toFixed(2)}s` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 一覧 */}
      <div className="divide-y border rounded">
        {runs.map(run => (
          <div key={run.id}>
            <button className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-slate-50 ${run.status !== 'completed' ? 'opacity-50' : ''}`} onClick={() => loadItems(run.id)}>
              {expandedRun === run.id ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
              <span className="text-slate-500">{fmtDate(run.created_at)}</span>
              <span className="font-mono text-slate-600">{run.model}</span>
              {run.mode === 'stability' && <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-slate-100 text-slate-600">安定性</span>}
              {run.status !== 'completed' && <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-red-50 text-red-600">中止・無効</span>}
              {PARAM_BADGES.filter(({ key }) => run[key] && run[key] !== 'default').map(({ key, label, className }) => (
                <span key={key} className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${className}`}>{label}:{run[key]}</span>
              ))}
              <span className="text-green-600 font-bold">{run.match_count}P</span>
              {run.error_count > 0 && <span className="text-red-500">{run.error_count}E</span>}
              <span className="text-slate-400 ml-auto">{run.label_count}件</span>
              {run.latency_median_ms && <span className="text-slate-400">⏱中央値{(run.latency_median_ms / 1000).toFixed(1)}s</span>}
              {run.duration_ms && <span className="text-slate-400">{(run.duration_ms / 1000).toFixed(0)}s</span>}
            </button>
            {expandedRun === run.id && (
              <div className="px-3 pb-2 bg-slate-50 space-y-1">
                <div className="flex gap-2 mb-1">
                  <button onClick={() => exportRunCSV(runItems, run)} className="text-[10px] px-2 py-0.5 bg-green-50 text-green-700 rounded hover:bg-green-100 flex items-center gap-1">
                    <Download className="w-3 h-3" /> CSV
                  </button>
                  <button onClick={() => { navigator.clipboard.writeText(JSON.stringify(runItems, null, 2)); }} className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100">
                    JSON コピー
                  </button>
                </div>
                {runItems.map((item, i) => (
                  <div key={i} className={`text-[10px] px-2 py-1 rounded ${item.error ? 'bg-yellow-50' : item.match ? 'bg-white' : 'bg-red-50'}`}>
                    <span className="font-medium">{item.label}</span>
                    {item.error ? <span className="ml-2 text-yellow-600">{item.error}</span> :
                      <span className={`ml-2 ${item.match ? 'text-green-600' : 'text-red-600'}`}>
                        {item.expected_status != null
                          ? <>{item.expected_status}→{item.actual_status} {item.match ? '✓' : '✗'}</>
                          : <>{item.reason} {item.match ? '✓' : '✗'}</>}
                        {item.analyze_ms && <span className="text-slate-400 ml-1">⏱{(item.analyze_ms / 1000).toFixed(1)}s</span>}
                      </span>
                    }
                    {/* 誤答の原因切り分け用: reason文と最終判定が食い違って見えるとき、
                        api/_validateResponse.js が上書きに使った内訳を表示する */}
                    {!item.match && !item.error && item.expected_status != null && (item.label_consistency || item.quantity_status) && (
                      <div className="mt-0.5 pl-2 text-slate-500">
                        ラベル整合:{item.label_consistency || '—'} ／ 個数照合:{item.quantity_status || '—'}
                        {(item.label_quantity_number != null || item.detected_quantity != null) &&
                          <> （ラベル{item.label_quantity_number ?? '?'} / 検出{item.detected_quantity ?? '?'}）</>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
